import { useEffect, useState, type CSSProperties } from "react"
import { motion, AnimatePresence } from "motion/react"
import PromoCodeSheet from "./PromoCodeSheet"

const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type BonusSpendBlockProps = {
    balance: number
    totalPrice: number
    seatCount: number
    /** Доля кэшбека с карты, например 0.15 = 15% */
    percents: number
    bonusPay: number
    onBonusPayChange: (value: number) => void
}

function AnimatedNumber({ value, className, style }: { value: number; className?: string; style?: CSSProperties }) {
    return (
        <span className={`inline-block tabular-nums ${className ?? ""}`} style={style}>
            <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                    key={value}
                    initial={{ opacity: 0, y: 6, filter: "blur(2px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -6, filter: "blur(2px)" }}
                    transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
                    className="inline-block">
                    {value}
                </motion.span>
            </AnimatePresence>
        </span>
    )
}

export default function BonusSpendBlock({
    balance,
    totalPrice,
    seatCount,
    percents,
    bonusPay,
    onBonusPayChange,
}: BonusSpendBlockProps) {
    const maxBonus = Math.min(balance, Math.max(0, totalPrice - seatCount))
    // Как на бэке getBonusAdd: amount(руб) * movie_percents, HALF_DOWN
    const earn = Math.max(0, Math.ceil((totalPrice - bonusPay) * percents - 0.5))
    const fillPct = maxBonus > 0 ? (bonusPay / maxBonus) * 100 : 0
    const disabled = balance === 0 || maxBonus === 0

    const [promoOpen, setPromoOpen] = useState(false)
    /* Локальный текст поля ввода: даёт стирать/печатать свободно, наружу уходит зажатое число */
    const [draft, setDraft] = useState(String(bonusPay))
    useEffect(() => {
        setDraft(String(bonusPay))
    }, [bonusPay])

    /* «Прилипание» ползунка к 0 / половине / максимуму */
    const snapPoints = [0, Math.floor(maxBonus / 2), maxBonus]
    const snapThreshold = Math.max(3, Math.round(maxBonus * 0.05))
    const handleSlider = (raw: number) => {
        let value = raw
        for (const point of snapPoints) {
            if (Math.abs(raw - point) <= snapThreshold) {
                value = point
                break
            }
        }
        onBonusPayChange(value)
    }

    const commitDraft = (text: string) => {
        const digits = text.replace(/\D/g, "")
        setDraft(digits)
        const value = digits === "" ? 0 : Math.min(maxBonus, parseInt(digits, 10))
        onBonusPayChange(value)
    }

    return (
        <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className={`relative shrink-0 overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.04] p-4 ${disabled ? "opacity-60" : ""}`}>
            <div
                className="pointer-events-none absolute -right-8 -top-8 size-28 overflow-hidden rounded-full blur-2xl"
                style={{
                    background: bonusPay > 0 ? "rgba(41,204,106,0.18)" : "rgba(52,155,230,0.12)",
                    transition: "background 0.3s ease",
                }}
            />

            {/* Заголовок + баланс в одну строку */}
            <div className="relative mb-4 flex items-baseline justify-between gap-3">
                <span
                    className="text-[10px] uppercase tracking-[0.08em] text-white/40"
                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                    Бонусы на карте
                </span>
                <span
                    className="text-[18px] leading-none text-[#29cc6a]"
                    style={{ fontFamily: MONO, fontWeight: 700, textShadow: "0 0 18px rgba(41,204,106,0.35)" }}>
                    <AnimatedNumber value={balance} />
                </span>
            </div>

            {disabled ? (
                <p className="relative text-[12px] text-white/45" style={{ fontFamily: BODY, fontWeight: 500 }}>
                    Бонусов нет или их нельзя применить к этому заказу
                </p>
            ) : (
                <>
                    {/* Списать (ввод) → начислится */}
                    <div className="relative mb-3 flex items-center justify-between gap-3">
                        <label
                            className="flex min-w-0 items-baseline gap-2"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            <span className="shrink-0 text-[10px] uppercase tracking-[0.06em] text-white/40">Списать</span>
                            <motion.span
                                className="rounded-[10px] border px-1 py-0.5"
                                animate={{
                                    borderColor: bonusPay > 0 ? "rgba(228,32,107,0.4)" : "rgba(255,255,255,0.12)",
                                    boxShadow: bonusPay > 0 ? "0 0 12px rgba(228,32,107,0.15)" : "0 0 0 rgba(0,0,0,0)",
                                }}
                                transition={{ duration: 0.2 }}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={draft}
                                    onChange={e => commitDraft(e.target.value)}
                                    onBlur={() => setDraft(String(bonusPay))}
                                    onFocus={e => {
                                        const target = e.target
                                        target.select()
                                        // Ждём, пока клавиатура откроется и шторка приподнимется
                                        window.setTimeout(() => {
                                            target.scrollIntoView({ block: "center", behavior: "smooth" })
                                        }, 350)
                                    }}
                                    className="w-[64px] bg-transparent text-center text-[18px] tabular-nums text-[#e4206b] outline-none"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}
                                    aria-label="Сколько бонусов списать"
                                />
                            </motion.span>
                        </label>
                        <span
                            className="shrink-0 text-right text-[11px] leading-tight text-white/40"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            начислится{" "}
                            <span className="text-[#349be6]">
                                <AnimatedNumber value={earn} />
                            </span>
                        </span>
                    </div>

                    {/* Слайдер с прилипающим ползунком */}
                    <div className="relative mb-1 h-8">
                        <div className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full bg-white/10">
                            <motion.div
                                className="h-full rounded-full"
                                style={{ background: "linear-gradient(90deg, #29cc6a 0%, #349be6 100%)" }}
                                animate={{ width: `${fillPct}%` }}
                                transition={{ type: "spring", stiffness: 320, damping: 28 }}
                            />
                        </div>
                        {/* Метки точек прилипания */}
                        {[0, 50, 100].map(pct => (
                            <div
                                key={pct}
                                className="pointer-events-none absolute top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/35"
                                style={{ left: `${pct}%` }}
                            />
                        ))}
                        <motion.div
                            className="pointer-events-none absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#349be6] shadow-[0_0_12px_rgba(52,155,230,0.5)]"
                            animate={{ left: `${fillPct}%` }}
                            transition={{ type: "spring", stiffness: 320, damping: 28 }}
                        />
                        <input
                            type="range"
                            min={0}
                            max={maxBonus}
                            step={1}
                            value={bonusPay}
                            onChange={e => handleSlider(Number(e.target.value))}
                            /* touch-action: none — иначе браузер отдаёт жест скроллу шторки
                               и бегунок на тачскрине не таскается */
                            className="absolute inset-0 z-10 w-full cursor-pointer opacity-0 [touch-action:none]"
                            aria-label="Сколько бонусов списать"
                        />
                    </div>

                    <div
                        className="flex justify-between text-[10px] tabular-nums text-white/30"
                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                        <span>0</span>
                        <span>{maxBonus}</span>
                    </div>
                </>
            )}

            {/* Промокод */}
            <button
                type="button"
                onClick={() => setPromoOpen(true)}
                className="relative mt-3 text-[12px] text-[#349be6] underline decoration-[#349be6]/40 underline-offset-4 transition-colors hover:text-[#5db4f0]"
                style={{ fontFamily: BODY, fontWeight: 600 }}>
                У меня есть промокод
            </button>

            {promoOpen && <PromoCodeSheet onClose={() => setPromoOpen(false)} />}
        </motion.section>
    )
}
